import { useCallback, useRef, useState } from "react";

/**
 * Wraps an (possibly async) action so a button can show a loading state while it
 * runs. Returns `loading` and a `run` handler. A `minDurationMs` floor keeps the
 * spinner on screen long enough to read even when the action resolves almost
 * instantly (e.g. a download that's triggered synchronously), so the swap-in /
 * swap-out animation never flickers. Re-entrancy is guarded via a ref.
 */
export function useAsyncAction(
  action: () => void | Promise<void>,
  minDurationMs = 700
): { loading: boolean; run: () => void } {
  const [loading, setLoading] = useState(false);
  const runningRef = useRef(false);

  const run = useCallback(() => {
    if (runningRef.current) return;
    runningRef.current = true;
    setLoading(true);
    const started =
      typeof performance !== "undefined" ? performance.now() : Date.now();
    Promise.resolve()
      .then(action)
      .catch(() => {
        // Errors are surfaced by the action itself; still clear the loading state.
      })
      .finally(() => {
        const now =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        const elapsed = now - started;
        const finish = () => {
          runningRef.current = false;
          setLoading(false);
        };
        if (elapsed < minDurationMs) {
          window.setTimeout(finish, minDurationMs - elapsed);
        } else {
          finish();
        }
      });
  }, [action, minDurationMs]);

  return { loading, run };
}
